'use client';

import { FC, useMemo } from 'react';

import { getRoute } from '@ravago/shared/next-data-access';
import { ElasticCaseStudy } from '@ravago/shared/page-data/models/elastic';
import { CaseStudyCard as CaseStudyCardModel, ProductPortfolioNode } from '@ravago/shared/page-data/models/elements';
import { RendererGlobals } from '@ravago/shared/page-data/models/utils';
import { DataMapperUtils } from '@ravago/shared/page-data/utils/data-mapper';
import { Badge } from '@ravago/shared/radiance/components/badge/components/Badge/Badge';
import { CardGrid } from '@ravago/shared/radiance/components/card/components/CardGrid/CardGrid';
import { CaseStudyCard } from '@ravago/shared/radiance/components/card/components/CaseStudyCard/CaseStudyCard';
import { useTranslations } from '@ravago/shared/translations/client';

import { AppUtils } from '../../../../../../utils/app/app.utils';

interface Props {
   baseRoutes?: {
      caseStudy?: string;
      market?: string;
      product?: string;
   };
   caseStudies: Array<ElasticCaseStudy>;
   globals: RendererGlobals;
   node: ProductPortfolioNode;
}

export const CaseStudyGrid: FC<Props> = ({ caseStudies, baseRoutes, node, globals }) => {
   const translate = useTranslations();

   const { assetBasePath, acceptWebp, locale } = globals;

   const caseStudyCards = useMemo(
      () => caseStudies.map((caseStudy) => DataMapperUtils.normalizeElasticCaseStudyCard(caseStudy, assetBasePath, acceptWebp)),
      [caseStudies, assetBasePath, acceptWebp]
   );

   return (
      <CardGrid>
         {caseStudyCards?.map((caseStudy: CaseStudyCardModel) => (
            <CaseStudyCard
               key={caseStudy.id}
               href={getRoute(locale, baseRoutes?.caseStudy, caseStudy.slug, undefined)}
               target="_self"
               noFollow={caseStudy.noFollow}
               title={caseStudy.title}
               excerpt={caseStudy.excerpt ?? ''}
               image={caseStudy.image}
               markets={caseStudy.markets}
               products={caseStudy.products}
               badge={
                  <>
                     {caseStudy.isConfidential && <Badge variant="warning">{translate('case-study-confidential')}</Badge>}
                     {!caseStudy.isConfidential && caseStudy.visibility === 'Internal' && (
                        <Badge variant="warning">{translate('case-study-internal-only')}</Badge>
                     )}
                  </>
               }
               attributes={{ 'test-selector': AppUtils.getTestSelector(`case-study-${caseStudy.title}`, node.type, node.settings?.testId) }}
            />
         ))}
      </CardGrid>
   );
};
